import { useCallback } from "react";

import { useFirebaseContext } from "contexts/FirebaseContext";
import { usePitPortClient } from "hooks/api/usePitPortClient";
import { useSwr } from "hooks/useSwr";
import { PIT_PORT_PAGINATION_DEFAULT_INDEX } from "pages/PitPort/constants/pagination";
import { OwnerEntityErrorResponse } from "pages/PitPort/types";
import { formatQueryParams } from "utils/stringUtils";

/**
 * 貸主エンティティを取得する
 * @param pageIndex
 * @param keyword
 * @returns PitPortOwnerEntitiesResponse
 */
export const useFindOwnerEntities = ({
  pageIndex,
  keyword,
  pageSize,
}: {
  pageIndex: number;
  keyword: string;
  pageSize?: number;
}) => {
  const { data, error } = useSwr<OwnerEntitiesResponse>(
    `/owner-entities?${formatQueryParams({
      pageIndex: Number(pageIndex) ?? PIT_PORT_PAGINATION_DEFAULT_INDEX,
      pageSize: pageSize,
      keyword: keyword ?? "",
    })}`
  );

  return {
    data: data,
    error,
  };
};

/**
 * 貸主エンティティを取得する
 * @param ownerEntityId
 * @returns OwnerEntity
 */
export const useFindOwnerEntity = ({ ownerEntityId }: { ownerEntityId: string }) => {
  const { data, error } = useSwr<{ data: OwnerEntity }>(`/owner-entities/${ownerEntityId}`);

  return {
    data: data,
    error,
  };
};

/**
 * 貸主ユーザーを取得する
 * @param ownerEntityId
 * @returns OwnerUser[]
 */
export const useFindOwnerUsers = ({
  ownerEntityId,
  pageIndex,
  pageSize,
  keyword,
}: {
  ownerEntityId: string;
  pageIndex: number;
  pageSize: number;
  keyword: string;
}) => {
  const { data, error } = useSwr<OwnerUsersResponse>(
    `/owner-entities/${ownerEntityId}/owner-users?${formatQueryParams({
      pageIndex: pageIndex ?? PIT_PORT_PAGINATION_DEFAULT_INDEX,
      pageSize: pageSize,
      keyword: keyword ?? "",
    })}`
  );

  return {
    data: data,
    error,
  };
};

/**
 * 貸主エンティティを更新する
 * @param ownerEntityId
 * @param ownerEntity
 * @returns OwnerEntity
 */
export const useUpdateOwnerEntity = () => {
  const { auth } = useFirebaseContext();
  const { client } = usePitPortClient();

  const update = useCallback(
    async (ownerEntity: OwnerEntity) => {
      const token = await auth?.currentUser?.getIdToken();
      if (!token) return { status: 401 };

      const { status, data }: OwnerEntityErrorResponse = await client({
        method: "put",
        url: `/owner-entities/${ownerEntity.id}`,
        data: ownerEntity,
      });

      return { status, message: data.message };
    },
    [auth?.currentUser, client]
  );

  return { update };
};

/**
 * 貸主エンティティを作成する
 * @param data
 * @returns
 */
export const useCreateOwnerEntity = () => {
  const client = usePitPortClient();

  const create = useCallback(
    async (data: {
      name: string;
      address: string;
      tel: string;
      email: string;
      type: "individual_ps" | "corporation_ps" | "corporation_oem";
      profitShareRate: number;
    }) => {
      return await client.client({
        method: "POST",
        url: "/owner-entities",
        data,
      });
    },
    [client]
  );

  return { create };
};
